import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle, Loader2, Truck, Package,
  AlertTriangle, Sparkles, Bot, MapPin, Radio, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * OrderDetail — /transactions/order/:id
 *
 * Shows the formal order + its state-machine timeline + optional
 * transport chain. All actions (request transport, accept transport
 * offer, complete order) go through backend state validators; the UI
 * never advances state on its own.
 *
 * Payment is intentionally NOT wired here. `FINDPAY_PROVIDER=mock`
 * remains authoritative until compliance clears production activation.
 */
const TIMELINE_STATES = [
  "AGREED", "ORDERED",
  "TRANSPORT_REQUIRED", "TRANSPORT_ASSIGNED",
  "PICKUP", "IN_TRANSIT", "DELIVERED", "COMPLETED",
];

function fmtMoney(v, cur = "INR") {
  if (v == null) return "—";
  return (cur === "INR" ? "₹" : cur + " ") +
         Number(v).toLocaleString("en-IN");
}

export default function OrderDetail() {
  const nav = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get(`/transactions/orders/${id}`);
      setData(r.data);
    } catch (e) {
      setData(null);
      if (e?.response?.status === 401) toast.error("Please log in.");
    } finally {
      setBusy(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Poll every 15s while IN_TRANSIT so buyer sees fresh GPS pings.
  useEffect(() => {
    if (!data?.order) return;
    const state = data.order.state;
    if (!["PICKUP", "IN_TRANSIT"].includes(state)) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [data?.order?.state, load]);

  const order = data?.order;
  const tr    = data?.transport_request;
  const toffs = data?.transport_offers || [];
  const delivery = data?.delivery;

  const requestTransport = async () => {
    setActionBusy(true);
    try {
      await api.post(`/transactions/orders/${order.order_id}/request-transport`);
      toast.success("Transport requested — waiting for offers");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't request transport");
    } finally { setActionBusy(false); }
  };

  const acceptTransportOffer = async (toffId) => {
    setActionBusy(true);
    try {
      await api.post(`/transactions/transport-offers/${toffId}/accept`);
      toast.success("Transport assigned");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't accept transport");
    } finally { setActionBusy(false); }
  };

  const completeOrder = async () => {
    setActionBusy(true);
    try {
      const r = await api.post(`/transactions/orders/${order.order_id}/complete`);
      const next = (r.data.next_need_suggestions || []).map((s) => s.label).join(", ");
      toast.success("Order completed" + (next ? ` · Next: ${next}` : ""));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't complete order");
    } finally { setActionBusy(false); }
  };

  if (busy && !order) {
    return <div className="min-h-[70vh] grid place-items-center text-stone-500"><Loader2 className="animate-spin"/></div>;
  }
  if (!order) {
    return (
      <div className="min-h-[70vh] grid place-items-center text-stone-500 text-sm">
        Order not found. <button className="ml-2 underline" onClick={() => nav("/transactions/orders")}>Back</button>
      </div>
    );
  }

  const isBuyer = order.buyer_id && data?.order && !order.state ? false : true;
  const timelineIndex = TIMELINE_STATES.indexOf(order.state);
  const done = order.state === "COMPLETED";
  const cancelled = order.state === "CANCELLED";

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-stone-50 pb-20" data-testid="txn-order-detail">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-stone-200">
        <div className="max-w-2xl mx-auto px-3 py-2 flex items-center gap-2">
          <button onClick={() => nav(-1)} className="p-2 -ml-1 rounded-full hover:bg-stone-100" data-testid="txn-order-back">
            <ArrowLeft size={18}/>
          </button>
          <div className="font-black text-stone-900 truncate">Order · {order.product}</div>
          <div className={`ml-auto text-[10px] uppercase font-black px-2 py-1 rounded-full ${
            done ? "bg-emerald-600 text-white" :
            cancelled ? "bg-stone-500 text-white" : "bg-blue-600 text-white"
          }`} data-testid="txn-order-state">
            {order.state}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-3 space-y-3">
        {/* AINA proactive nudge — visible whenever transport is required or offers are available */}
        {order.state === "TRANSPORT_REQUIRED" && (
          <div className="bg-gradient-to-br from-orange-50 to-rose-50 border border-orange-200 rounded-2xl p-4 flex items-start gap-3" data-testid="txn-aina-nudge">
            <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-[#E05E36] to-[#c94e2a] grid place-items-center text-white shadow shrink-0">
              <Bot size={18}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-black text-orange-700">AINA suggests</div>
              <div className="font-black text-stone-900 text-sm mt-0.5">
                Your {order.quantity}&nbsp;{order.unit} of {order.product} needs transport.
              </div>
              <div className="text-[11px] text-stone-600 mt-0.5">
                {toffs.length > 0
                  ? `${toffs.length} offer${toffs.length !== 1 ? "s" : ""} received. Review below.`
                  : "Waiting for transport providers to bid. Nothing is booked until you accept."}
              </div>
              <div className="text-[10px] text-stone-400 italic mt-1">
                AINA never accepts an offer or commits money on your behalf.
              </div>
            </div>
          </div>
        )}

        {/* Summary card */}
        <div className="bg-white border border-stone-200 rounded-2xl p-4 space-y-1.5">
          <div className="text-[10px] uppercase tracking-widest font-black text-stone-500">Product</div>
          <div className="text-lg font-black text-stone-900">{order.product}</div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone-600 font-bold">
            <span>Quantity: <b>{order.quantity} {order.unit}</b></span>
            <span>Price: <b>{fmtMoney(order.price, order.currency)} / {order.unit}</b></span>
            <span>Subtotal: <b className="text-emerald-700">{fmtMoney(order.subtotal, order.currency)}</b></span>
          </div>
          {/* Reference conversion (never overwrites original amount) */}
          {data?.reference_conversion?.available && Object.keys(data.reference_conversion.conversions || {}).length > 0 && (
            <div className="text-[10px] text-stone-500 pt-1" data-testid="txn-reference-conversion">
              <div>
                <span className="uppercase font-black tracking-wide text-stone-600">Reference (indicative)</span>
                {": "}
                {Object.entries(data.reference_conversion.conversions).map(
                  ([cur, val]) => `${cur === "INR" ? "₹" : cur + " "}${Number(val).toLocaleString("en-IN")}`
                ).join(" · ")}
              </div>
              <div className="italic mt-0.5">
                Source: {data.reference_conversion.source} · Rates as of {new Date(data.reference_conversion.timestamp).toLocaleTimeString()}
                {data.reference_conversion.conversions?.WFC != null && (
                  <> · WFC = <b>{data.reference_conversion.wfc_label}</b> (not legal tender)</>
                )}
              </div>
              <div className="text-stone-400">Original {order.currency} amount stays authoritative.</div>
            </div>
          )}
          <div className="text-[10px] text-stone-500 pt-1">
            Payment: <span className="font-bold">MOCK ONLY</span> — agreement does NOT trigger payment.
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white border border-stone-200 rounded-2xl p-4">
          <div className="text-[10px] uppercase tracking-widest font-black text-stone-500 mb-2">Timeline</div>
          <div className="space-y-1.5" data-testid="txn-order-timeline">
            {TIMELINE_STATES.map((s, idx) => {
              const passed = idx <= timelineIndex;
              const active = idx === timelineIndex;
              const Ic = passed ? CheckCircle2 : Circle;
              return (
                <div key={s} className={`flex items-center gap-2 text-xs ${passed ? "text-stone-900" : "text-stone-400"}`}>
                  <Ic size={14} className={passed ? "text-emerald-600" : "text-stone-300"}/>
                  <span className={active ? "font-black" : "font-bold"}>{s.replace("_", " ")}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        {!done && !cancelled && (
          <div className="bg-white border border-stone-200 rounded-2xl p-4 space-y-2">
            {order.state === "ORDERED" && order.delivery_required && !tr && (
              <button onClick={requestTransport} disabled={actionBusy}
                      className="w-full py-2.5 rounded-full bg-blue-600 text-white font-black text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
                      data-testid="txn-request-transport">
                <Truck size={14}/> Request transport
              </button>
            )}
            {tr && !delivery && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black mb-1.5">
                  Transport offers ({toffs.length})
                </div>
                {toffs.length === 0 && (
                  <div className="text-xs text-stone-400 py-2 text-center">
                    Waiting for transport providers to bid…
                  </div>
                )}
                <div className="space-y-1.5">
                  {toffs.map((o) => (
                    <div key={o.transport_offer_id} className="flex items-center gap-2 bg-stone-50 rounded-xl p-2 text-xs">
                      <div className="flex-1">
                        <div className="font-black text-stone-900">{fmtMoney(o.price, o.currency)}</div>
                        {o.eta_hours && <div className="text-[10px] text-stone-500">ETA {o.eta_hours}h</div>}
                      </div>
                      <button onClick={() => acceptTransportOffer(o.transport_offer_id)}
                              disabled={actionBusy}
                              className="px-3 py-1 rounded-full bg-emerald-600 text-white font-black text-[10px]"
                              data-testid={`txn-accept-transport-${o.transport_offer_id}`}>
                        ACCEPT
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {order.state === "DELIVERED" && (
              <button onClick={completeOrder} disabled={actionBusy}
                      className="w-full py-2.5 rounded-full bg-emerald-600 text-white font-black text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
                      data-testid="txn-complete-order">
                <Package size={14}/> Mark complete
              </button>
            )}
          </div>
        )}

        {/* Delivery live tracking (P4) — visible whenever there's a delivery record */}
        {delivery && ["TRANSPORT_ASSIGNED", "PICKUP", "IN_TRANSIT", "DELIVERED"].includes(order.state) && (
          <LiveTrackingCard
            delivery={delivery}
            orderState={order.state}
            onChange={load}
          />
        )}

        {done && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
            <div className="inline-flex items-center gap-1.5 text-emerald-800 font-black">
              <Sparkles size={16}/> Order complete
            </div>
            <div className="text-xs text-emerald-800 mt-1">
              This order may lead to a next need. Tap a suggestion to search for it — nothing is bought automatically.
            </div>
          </div>
        )}

        {cancelled && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-amber-900 text-xs inline-flex items-center gap-1.5">
            <AlertTriangle size={14}/> This order was cancelled.
          </div>
        )}
      </div>
    </div>
  );
}



/* --------------------------------------------------------------------------
 * P4 · Delivery Live Tracking Card
 * Shows last-known GPS + gives the transporter an opt-in toggle and a
 * "Share current location" button. The buyer sees the last ping. If the
 * transporter hasn't enabled sharing, we render a privacy notice.
 * ------------------------------------------------------------------------- */
function LiveTrackingCard({ delivery, orderState, onChange }) {
  const [toggling, setToggling] = useState(false);
  const [pinging, setPinging]   = useState(false);
  const [autoShare, setAutoShare] = useState(false);
  const autoTimerRef = useRef(null);

  const enabled = !!delivery.gps_share_enabled;
  const canPing = ["PICKUP", "IN_TRANSIT"].includes(orderState);
  const lastPing = delivery.last_ping || (Array.isArray(delivery.gps_history)
    ? delivery.gps_history[delivery.gps_history.length - 1]
    : null);

  const toggle = async (next) => {
    setToggling(true);
    try {
      await api.post(`/transactions/deliveries/${delivery.delivery_id}/gps-share`,
                     { enabled: next });
      toast.success(next ? "Live tracking enabled" : "Live tracking off");
      if (!next) { stopAuto(); }
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update sharing");
    } finally { setToggling(false); }
  };

  const pingNow = async () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setPinging(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await api.post(`/transactions/deliveries/${delivery.delivery_id}/ping`, {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy_m: pos.coords.accuracy || null,
          });
          if (r.data?.throttled) {
            toast(`Throttled — retry in ${r.data.retry_after_s}s`);
          } else {
            toast.success("Location shared");
          }
          onChange?.();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Ping failed");
        } finally { setPinging(false); }
      },
      () => {
        toast.error("Location permission denied");
        setPinging(false);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 5000 },
    );
  };

  const stopAuto = () => {
    if (autoTimerRef.current) { clearInterval(autoTimerRef.current); autoTimerRef.current = null; }
    setAutoShare(false);
  };
  const startAuto = () => {
    setAutoShare(true);
    pingNow();
    autoTimerRef.current = setInterval(pingNow, 45000); // >30s backend min interval
  };
  useEffect(() => () => stopAuto(), []);

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4 space-y-2" data-testid="txn-live-tracking">
      <div className="flex items-center gap-2">
        <Radio size={14} className={enabled ? "text-emerald-600" : "text-stone-400"}/>
        <div className="text-[10px] uppercase tracking-widest font-black text-stone-500">Live tracking</div>
        <span className={`ml-auto text-[10px] font-black px-1.5 py-0.5 rounded-full ${
          enabled ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-500"
        }`} data-testid="txn-live-tracking-state">
          {enabled ? "SHARING" : "OFF"}
        </span>
      </div>

      {!enabled && (
        <div className="text-[11px] text-stone-500 flex items-start gap-1.5">
          <ShieldOff size={12} className="mt-0.5"/>
          <span>Location is private until the transporter turns on live sharing.</span>
        </div>
      )}

      {enabled && lastPing && (
        <div className="text-xs text-stone-700" data-testid="txn-live-last-ping">
          <MapPin size={12} className="inline mr-1 text-emerald-600"/>
          {Number(lastPing.lat).toFixed(4)}, {Number(lastPing.lng).toFixed(4)}
          <span className="ml-1 text-stone-400">· {new Date(lastPing.at).toLocaleTimeString()}</span>
          {lastPing.accuracy_m != null && (
            <span className="ml-1 text-stone-400">· ±{Math.round(lastPing.accuracy_m)}m</span>
          )}
          {delivery.is_stale && (
            <span
              data-testid="txn-live-stale"
              className="ml-2 inline-block px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-black text-[9px] uppercase">
              Stale · {Math.round((delivery.last_ping_age_s || 0) / 60)}m ago
            </span>
          )}
        </div>
      )}
      {enabled && delivery.gps_session_expires_at && (
        <div className="text-[10px] text-stone-400" data-testid="txn-live-session">
          Session expires {new Date(delivery.gps_session_expires_at).toLocaleString()}
        </div>
      )}

      <div className="pt-1 space-y-1.5">
        <button
          data-testid="txn-live-toggle"
          disabled={toggling}
          onClick={() => toggle(!enabled)}
          className={`w-full py-2 rounded-full text-white font-black text-xs disabled:opacity-40 ${
            enabled ? "bg-stone-700" : "bg-emerald-600"
          }`}>
          {toggling ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1"/> : null}
          {enabled ? "Turn off live tracking" : "Enable live tracking"}
        </button>
        {enabled && canPing && (
          <div className="grid grid-cols-2 gap-2">
            <button
              data-testid="txn-live-ping"
              disabled={pinging}
              onClick={pingNow}
              className="py-2 rounded-full bg-emerald-100 text-emerald-800 font-black text-xs disabled:opacity-40">
              {pinging ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1"/> : <MapPin size={12} className="inline mr-1"/>}
              Share now
            </button>
            <button
              data-testid="txn-live-auto"
              onClick={autoShare ? stopAuto : startAuto}
              className={`py-2 rounded-full font-black text-xs ${
                autoShare ? "bg-rose-100 text-rose-700" : "bg-stone-100 text-stone-700"
              }`}>
              {autoShare ? "Stop auto" : "Auto (45s)"}
            </button>
          </div>
        )}
      </div>
      <div className="text-[10px] text-stone-400 italic pt-0.5">
        Only the transporter can enable sharing. History is capped to the most recent 500 points.
      </div>
    </div>
  );
}
